import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  withStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { connect } from 'react-redux';
import { appService } from '../../App/app.service';
import ForecastTrendChart from './ForecastTrendChart';
import { CumulativeClosedWonChart, PipelineByStageChart, ForecastAccuracyChart } from './DealProgressionCharts';
import { PipelineAgingChart, ConversionCycletimeChart, QualificationConversionChart, POCConversionChart } from './DealConversionCharts';
import UpsellCrossSellChart from './UpsellCrossSellChart';
import QualifiedPipelineCoverageChart from '../PipelineReviewPackage/QualifiedPipelineCoverageChart';
import SalesForecastCurrentQuarterTable from './SalesForecastCurrentQuarterTable';
import { countOrACVFormatterMillionFixedOne, dateFormatterMomentV3 } from '../../util/customFunctions';
import { flexToWidth } from '../../util/utils';
import * as d3 from 'd3';
import { updatedScheme10 } from '../../util/chartColorScheme';
import RegionalSalesReviewPackagePPTX from '../../components/GlobalPPTXFiles/RegionalSalesReviewPackagePPTX';
import { ResizableTable } from '../../components/resizableTable/ResizableTable';
import AddHeaderDescriptionWithHelperText from '../../components/HelperTextDescription';
import SingleLevelDrilldown from '../../components/Drilldown/SingleLevelDrilldown';
import Drilldown from '../../components/Drilldown/Drilldown';
import DrilldownWithSLD from '../../components/Drilldown/DrilldownWithSLD';
import ExpectedAcvPage from '../NewWaterFall/ExpectedAcvPage';
import { getRegionalCard } from './RegionalCardRegistry';
// import RegionalSalesReviewPackagePPTX from './RegionalSalesReviewPackagePPTX';


const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#fafafa',
    minHeight: '100vh',
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    color: '#333',
    fontSize: '1.2rem',
  },
  subtitle: {
    color: '#666',
    fontSize: '0.875rem',
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chartCard: {
    height: '100%',
    position: 'relative',
  },
  chartContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 250,
    width: '100%',
  },
  legend: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  legendBox: {
    width: 16,
    height: 16,
    borderRadius: 2,
  },
  statusBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  elapsedBadge: {
    backgroundColor: '#9e9e9e',
    color: '#fff',
  },
  currentBadge: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  futureBadge: {
    backgroundColor: '#2196f3',
    color: '#fff',
  },
  summaryCard: {
    backgroundColor: '#fff',
    marginTop: theme.spacing(2),
  },
  summaryItem: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  summaryValue: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1976d2',
  },
  summaryLabel: {
    fontSize: '0.875rem',
    color: '#666',
  },
  updateFrequency: {
    backgroundColor: '#00bcd4',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  currentQuarterInfo: {
    marginTop: theme.spacing(1),
  },
  // Paper 2 Table Styles
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    backgroundColor: '#1976d2',
    '& th': {
      color: '#fff',
      fontWeight: 600,
      fontSize: '0.875rem',
      padding: '12px 8px',
      borderRight: '1px solid rgba(255,255,255,0.2)',
      '&:last-child': {
        borderRight: 'none',
      },
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa',
    },
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  newOpportunityRow: {
    backgroundColor: '#c8e6c9 !important',
    '&:hover': {
      backgroundColor: '#a5d6a7 !important',
    },
  },
  tableCell: {
    fontSize: '0.813rem',
    padding: '8px',
    borderRight: '1px solid #e0e0e0',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  summaryRow: {
    backgroundColor: '#f5f5f5',
    '& td': {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  targetRow: {
    backgroundColor: '#fff3cd',
    '& td': {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  notesBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#fffbea',
    borderRadius: 4,
    border: '1px solid #ffd54f',
  },
  noteItem: {
    fontSize: '0.75rem',
    marginBottom: theme.spacing(0.5),
  },
  leftNote: {
    backgroundColor: '#fff9e6',
    padding: theme.spacing(2),
    borderRadius: 4,
    fontSize: '0.75rem',
    lineHeight: 1.6,
  },
  filterWrapper: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  filterContainer: {
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'inline-flex',
    width: 'auto',
  },
  formControl: {
    minWidth: 200,
  },
  teamFilterFormControl: {
    minWidth: 200,
  },
  goButton: {
    borderRadius: 4,
    textTransform: 'none',
    fontWeight: 600,
    padding: '8px 24px',
  },
  filterLabel: {
    color: '#000',
  },
  filterLabelActive: {
    color: '#4472c4',
  },
});

const stickyHeaderStyle = {
  backgroundColor: '#1976d2',
  color: '#fff',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '12px 8px',
  borderRight: '1px solid rgba(255,255,255,0.2)',
  textAlign: 'center',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

// Helper function to calculate column width based on text length
// Caps at 80 characters - longer text will wrap instead of expanding column
const calculateColumnWidth = (textLength) => {
  // Cap text length at 80 characters for width calculation
  const cappedLength = Math.min(textLength, 80);
  
  if (cappedLength >= 0 && cappedLength <= 2) {
    return 48;
  } else if (cappedLength >= 3 && cappedLength <= 5) {
    return 60;
  } else if (cappedLength >= 6 && cappedLength <= 10) {
    return 80;
  } else if (cappedLength >= 11 && cappedLength <= 20) {
    return 120;
  } else if (cappedLength >= 21 && cappedLength <= 30) {
    return 180;
  } else if (cappedLength >= 31 && cappedLength <= 45) {
    return 240;
  } else if (cappedLength >= 46 && cappedLength <= 60) {
    return 352;
  } else if (cappedLength > 60 && cappedLength <= 80) {
    return 500; // Width for ~80 characters (text will wrap if longer)
  }
  return 120; // default
};

// Helper function to calculate default width for a column based on row data
const calculateDefaultWidthForField = (field, rows, getCellValue) => {
  let maxLength = field.length; // Start with header length
  
  if (rows && rows.length > 0) {
    // Sample up to 20 rows to calculate max length
    const sampleRows = rows.slice(0, 20);
    sampleRows.forEach((row) => {
      let value = row[field];
      
      // Use getCellValue if provided to get formatted value
      if (getCellValue) {
        const formattedValue = getCellValue(row, field);
        value = formattedValue != null ? String(formattedValue) : '';
      } else {
        value = value != null ? String(value) : '';
      }
      
      const stringValue = String(value || '').trim();
      // Cap the length at 80 characters for width calculation
      maxLength = Math.max(maxLength, Math.min(stringValue.length, 40));
    });
  }
  
  // Cap final length at 80 characters
  maxLength = Math.min(maxLength, 40);
  
  return calculateColumnWidth(maxLength);
};

// Helper function to create columns for opportunity tables with dynamic width calculation
const createOpportunityTableColumns = (rows = [], getCellValue = null, currencySymbol = '') => {
  // Calculate widths for each field based on actual data
  const rowNumWidth = 40; // Fixed width for row number
  const opportunityWidth = calculateDefaultWidthForField('opportunity', rows, getCellValue);
  const ownerWidth = calculateDefaultWidthForField('owner', rows, getCellValue);
  const executiveSponsorWidth = calculateDefaultWidthForField('executiveSponsor', rows, getCellValue);
  const acvInKWidth = calculateDefaultWidthForField('acvInK', rows, getCellValue);
  const discountPercentWidth = 40; // Fixed width for percentage
  const projectedCloseDateWidth = calculateDefaultWidthForField('projectedCloseDate', rows, getCellValue);
  const ageSinceQualDaysWidth = calculateDefaultWidthForField('ageSinceQualDays', rows, getCellValue);
  const stageCharWidth = 50;
  const daysInStageWidth = 50;
  const partnerWidth = calculateDefaultWidthForField('partner', rows, getCellValue);
  const forecastCategoryWidth = calculateDefaultWidthForField('forecastCategory', rows, getCellValue);
  const nextStepWidth = calculateDefaultWidthForField('nextStep', rows, getCellValue);
  const strategicAccountWidth = calculateDefaultWidthForField('Strategic_Account', rows, getCellValue);
  
  return [
  {
    label: '#',
    sortable: false,
    subColumns: [{
      label: '#',
      field: 'rowNum',
      type: 'number',
      align: 'center',
      width: 10,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Opp Name',
    sortable: true,
    subColumns: [{
      label: 'Opportunity',
      field: 'opportunity',
      type: 'text',
      align: 'left',
      width: 115,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Owner',
    sortable: true,
    subColumns: [{
      label: 'Owner',
      field: 'owner',
      type: 'text',
      align: 'left',
      width: 45,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Executive Sponsor',
    sortable: true,
    subColumns: [{
      label: 'Executive Sponsor',
      field: 'Executive_Sponsor',
      type: 'text',
      align: 'left',
      width: executiveSponsorWidth,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Cust. Type',
    sortable: true,
    subColumns: [{
      label: 'Cust. Type',
      field: 'Cust_Type',
      type: 'text',
      align: 'left',
      width: 37,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
   {
    label: 'Lead Source',
    sortable: true,
    subColumns: [{
      label: 'Lead Source',
      field: 'Lead_Source',
      type: 'text',
      align: 'left',
      width: 37,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Strategic Account',
    sortable: true,
    subColumns: [{
      label: 'Strategic Account',
      field: 'Strategic_Account',
      type: 'text',
      align: 'left',
      width: strategicAccountWidth,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: `ACV (${currencySymbol}K)`,
    sortable: true,
    subColumns: [{
      label: `ACV (${currencySymbol}K)`,
      field: 'acvInK',
      type: 'number',
      align: 'right',
      width: 40,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Disc. %',
    sortable: true,
    subColumns: [{
      label: 'Disc. %',
      field: 'discountPercent',
      type: 'percentage',
      align: 'right',
      width: 20,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Proj. Close Date',
    sortable: true,
    subColumns: [{
      label: 'Proj. Close Date',
      field: 'projectedCloseDate',
      type: 'date',
      align: 'right',
      width: 44,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Age since Qual. (Days)',
    sortable: true,
    subColumns: [{
      label: 'Age since Qual. (Days)',
      field: 'ageSinceQualDays',
      type: 'number',
      align: 'right',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Stage',
    sortable: true,
    subColumns: [{
      label: 'Stage',
      field: 'stageChar',
      type: 'text',
      align: 'center',
      width: 25 ,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Days in Stg.',
    sortable: true,
    subColumns: [{
      label: 'Days in Stg.',
      field: 'daysInStage',
      type: 'number',
      align: 'right',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Forecast Category',
    sortable: true,
    subColumns: [{
      label: 'Forecast Category',
      field: 'forecastCategory',
      type: 'text',
      align: 'left',
      width: 50,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Partner',
    sortable: true,
    subColumns: [{
      label: 'Partner',
      field: 'partner',
      type: 'text',
      align: 'left',
      width: 70,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Risks',
    sortable: false,
    subColumns: [{
      label: 'Risks',
      field: 'risks',
      type: 'text',
      align: 'left',
      width: 80,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Actions',
    sortable: false,
    subColumns: [{
      label: 'Actions',
      field: 'actions',
      type: 'text',
      align: 'left',
      width: 80,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  }
];
};

// Helper function to build ResizableTable columns from LKP_DRILLDOWN_CONFIG records
// Falls back to createOpportunityTableColumns when no config is provided
const buildColumnsFromConfig = (configColumns = [], rows = [], getCellValue = null, currencySymbol = '') => {
  if (!configColumns || configColumns.length === 0) return null;

  const columns = [
    {
      label: '#',
      sortable: false,
      subColumns: [{
        label: '#',
        field: 'rowNum',
        type: 'number',
        align: 'center',
        width: 10,
        sortable: false,
      }],
      headerBgColor: '#1976d2',
      headerTextColor: '#ffffff',
    },
  ];

  configColumns.forEach((col) => {
    let type = 'text';
    let align = 'left';
    if (col.type === 'currency' || col.type === 'integer') {
      type = 'number';
      align = 'right';
    } else if (col.type === 'percent') {
      type = 'percentage';
      align = 'right';
    } else if (col.type === 'date' || col.type === 'datetime') {
      type = 'date';
      align = 'right';
    }

    const width = col.flex != null ? flexToWidth(col.flex) : calculateDefaultWidthForField(col.field, rows, getCellValue);

    columns.push({
      label: col.label,
      sortable: true,
      subColumns: [{
        label: col.label,
        field: col.field,
        type,
        align,
        width,
        sortable: true,
        filterable: true,
      }],
      headerBgColor: '#1976d2',
      headerTextColor: '#ffffff',
    });
  });

  return columns;
};

// Helper function to transform opportunity data to rows
// Supports both raw DB field names (from LKP_DRILLDOWN_CONFIG driven queries) and
// legacy frontend field names (from old hardcoded compute mappings).
const transformOpportunityDataToRows = (opps, includeTotal = false, totalData = null) => {
  const rows = opps?.map(opp => {
    // Resolve each field: prefer raw DB name, fall back to legacy frontend name
    const oppName       = opp.OppName       ?? opp.opportunity ?? '';
    const ownerName     = opp.Owner_Name     ?? opp.owner       ?? '';
    const acvRaw        = opp.Acv_At_Time_T  ?? opp.ACV         ?? opp.acv ?? null;
    const acvInKVal     = opp.acvInK != null
      ? parseFloat(opp.acvInK)
      : (acvRaw != null ? acvRaw / 1000 : null);
    const projClose     = opp.Projected_Close_Date_At_Time ?? opp.Projected_Close_Date ?? opp.projectedCloseDate ?? '';
    const ageSinceQual  = opp.Days_Since_Qualified ?? opp.ageSinceQualDays ?? 0;
    const stageCharVal  = opp.stage_name_char  ?? opp.Stage_Char ?? opp.stageChar  ?? '';
    const daysInStageVal= opp.Days_at_Stage    ?? opp.daysInStage ?? null;
    const partnerVal    = opp.Partner_Name     ?? opp.partner    ?? '';
    const forecastCat   = opp.Forecast_Category_At_Time ?? opp.Forecast_Category ?? opp.forecastCategory ?? '';
    const nextStepVal   = opp.NextStep         ?? opp.nextStep   ?? '';
    const isNew         = opp.isNewThisWeek != null ? opp.isNewThisWeek : (opp.Is_New_This_Week === 'Y');
    const risksVal      = opp.Risk__c    ?? opp.RISK__C      ?? opp.risks      ?? '';
    const actionsVal    = opp.Action__c  ?? opp.ACTION__C      ?? opp.actions    ?? '';
    const oppIdVal      = opp.Opp_ID           ?? opp.oppId      ?? null;

    return {
      // Spread all raw DB fields so buildColumnsFromConfig can access any configured field by name
      ...opp,
      rowNum: opp.rowNum,
      // Legacy frontend field names (used by createOpportunityTableColumns fallback)
      opportunity: oppName,
      owner: ownerName,
      acvInK: acvInKVal,
      discountPercent: null,
      projectedCloseDate: projClose,
      ageSinceQualDays: ageSinceQual,
      stageChar: stageCharVal,
      daysInStage: daysInStageVal,
      partner: partnerVal,
      Cust_Type: opp.Cust_Type ?? '',
      Lead_Source: opp.Lead_Source ?? '',
      Strategic_Account: opp.Strategic_Account ?? '',
      forecastCategory: forecastCat,
      nextStep: nextStepVal,
      isNewThisWeek: isNew,
      risks: risksVal,
      actions: actionsVal,
      // Common aliases for buildColumnsFromConfig
      OppName: oppName,
      Owner_Name: ownerName,
      ACV: acvRaw,
      Projected_Close_Date: projClose,
      Days_Since_Qualified: ageSinceQual,
      Stage: stageCharVal,
      Days_at_Stage: daysInStageVal,
      Forecast_Category_Name: forecastCat,
      Partner_Name: partnerVal,
      RISK__C: risksVal,
      ACTION__C: actionsVal,
      // Drilldown identifiers
      oppId: oppIdVal,
      Opp_ID: opp.Opp_ID ?? oppIdVal,
      label: oppName,
    };
  }) || [];

  if (includeTotal && totalData) {
    rows.push({
      rowNum: null,
      opportunity: `Total (${totalData.totalCount} opps)`,
      OppName: `Total (${totalData.totalCount} opps)`,
      owner: null,
      Owner_Name: null,
      acvInK: totalData.totalACVInK,
      ACV: totalData.totalACVInK != null ? totalData.totalACVInK * 1000 : null,
      discountPercent: null,
      projectedCloseDate: null,
      Projected_Close_Date: null,
      ageSinceQualDays: null,
      Days_Since_Qualified: null,
      stageChar: null,
      Stage: null,
      daysInStage: null,
      Days_at_Stage: null,
      partner: null,
      Partner_Name: null,
      forecastCategory: null,
      Forecast_Category_Name: null,
      nextStep: null,
      isNewThisWeek: false,
      Strategic_Account: null,
      label: 'Total',
    });
  }

  return rows;
};

// Default column config for Forecast Opportunities when LKP_DRILLDOWN_CONFIG has no rows.
// field names match DEFAULT_FORECAST_SELECT_LIST in the backend controller. For Demo added need remove it


// Build ResizableTable column definitions from LKP_DRILLDOWN_CONFIG records (forecast opps)
const buildForecastColumnsFromConfig = (configColumns = []) => {
  const columns = [
    {
      label: '#',
      sortable: false,
      align: 'center',
      subColumns: [{ label: '#', field: 'rowNum', type: 'number', align: 'center', width: 10, sortable: false }],
      headerBgColor: '#1976d2',
      headerTextColor: '#ffffff',
    },
  ];
  configColumns.forEach((col) => {
    let type = 'text';
    let align = 'left';
    if (col.type === 'currency' || col.type === 'integer') { type = 'number'; align = 'right'; }
    else if (col.type === 'percent') { type = 'percentage'; align = 'right'; }
    else if (col.type === 'date' || col.type === 'datetime') { type = 'date'; align = 'right'; }
    const width = flexToWidth(col.flex);
    columns.push({
      label: col.label,
      sortable: true,
      align: 'center',
      subColumns: [{ label: col.label, field: col.field, type, align, width, sortable: true, filterable: true }],
      headerBgColor: '#1976d2',
      headerTextColor: '#ffffff',
    });
  });
  return columns;
};

// Helper function to create Win-Loss table columns
const createWinLossTableColumns = (currencySymbol = '') => [
  {
    label: 'Customer Name',
    sortable: true,
    subColumns: [{
      label: 'Customer Name',
      field: 'customerName',
      type: 'text',
      align: 'left',
      width: 100,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: `ACV (${currencySymbol}M)`,
    sortable: true,
    subColumns: [{
      label: `ACV (${currencySymbol}M)`,
      field: 'acvInMillions',
      type: 'number',
      align: 'right',
      width: 100,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Competition',
    sortable: true,
    subColumns: [{
      label: 'Competition',
      field: 'competition',
      type: 'text',
      align: 'left',
      width: 150,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Key Learnings',
    sortable: false,
    subColumns: [{
      label: 'Key Learnings',
      field: 'keyLearnings',
      type: 'text',
      align: 'left',
      width: 100,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

// Helper function to create summary table columns
const createSummaryTableColumns = (currencySymbol = '') => [
  {
    label: '',
    sortable: false,
    subColumns: [{
      label: '',
      field: 'label',
      type: 'text',
      align: 'left',
      width: 100,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: '# of Opps',
    sortable: false,
    subColumns: [{
      label: '# of Opps',
      field: 'count',
      type: 'number',
      align: 'right',
      width: 70,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: `ACV (${currencySymbol}M)`,
    sortable: false,
    subColumns: [{
      label: `ACV (${currencySymbol}M)`,
      field: 'acvInMillions',
      type: 'number',
      align: 'right',
      width: 70,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

// Helper function to create Renewals table columns
const createRenewalsTableColumns = (currencySymbol = '') => [
  {
    label: '#',
    sortable: false,
    subColumns: [{
      label: '#',
      field: 'rowNum',
      type: 'number',
      align: 'center',
      width: 20,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Customer Name',
    sortable: true,
    subColumns: [{
      label: 'Customer Name',
      field: 'customerName',
      type: 'text',
      align: 'left',
      width: 100,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Renewal Date',
    sortable: true,
    subColumns: [{
      label: 'Renewal Date',
      field: 'renewalDate',
      type: 'text',
      align: 'center',
      width: 80,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: `Contract Value (${currencySymbol}M)`,
    sortable: true,
    subColumns: [{
      label: `Contract Value (${currencySymbol}M)`,
      field: 'contractValue',
      type: 'number',
      align: 'right',
      width: 180,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Probability (%)',
    sortable: true,
    subColumns: [{
      label: 'Probability (%)',
      field: 'probability',
      type: 'number',
      align: 'center',
      width: 120,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Status',
    sortable: true,
    subColumns: [{
      label: 'Status',
      field: 'status',
      type: 'text',
      align: 'center',
      width: 120,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Risk Factors',
    sortable: false,
    subColumns: [{
      label: 'Risk Factors',
      field: 'riskFactors',
      type: 'text',
      align: 'left',
      width: 200,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Action Items',
    sortable: false,
    subColumns: [{
      label: 'Action Items',
      field: 'actionItems',
      type: 'text',
      align: 'left',
      width: 200,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

const RegionalSalesReviewPackage = (props) => {
const { classes, forecastTrendData, salesForecastCurrentQuarterData, forecastMovedOutData, forecastBestCaseData, salesForecastNextQuarterData, salesForecastNextQuarterPlus1Data, dealProgressionData, dealConversionData, upsellCrossSellData, qualifiedPipelineCoverageData, qualifiedPipelineCoverageDrilldownData, discoveryPipelineCoverageData, discoveryPipelineCoverageDrilldownData, rsrFirstLevelTeams, rsrSelectedTeam, rsrDefaultTeam, renewalsSummaryData, strategicAccountsQualifiedPipelineData, msspFulfillmentData } = props;

  // Local state for pending team selection (before clicking Go)
  const [pendingTeam, setPendingTeam] = useState('All');
  // Applied team filter (after clicking Go)
  const [appliedTeam, setAppliedTeam] = useState('All');

  // Customer Type filter state
  const [pendingCustType, setPendingCustType] = useState('All');
  const [appliedCustType, setAppliedCustType] = useState('All');

  // Track if initial data has been loaded
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // Drilldown state
  const [secondLevelLocation, setSecondLevelLocation] = useState('');
  // Forecast Trend drilldown state
  const [forecastTrendDrilldownLocation, setForecastTrendDrilldownLocation] = useState('');
  const [forecastTrendDrilldownFilters, setForecastTrendDrilldownFilters] = useState([]);
  // Qualified Pipeline Coverage drilldown state
  const [qualifiedPipelineDrilldownOpen, setQualifiedPipelineDrilldownOpen] = useState(false);
  const [qualifiedPipelineDrilldownFilters, setQualifiedPipelineDrilldownFilters] = useState([]);
  
  // Renewals Summary drilldown state
  const [renewalsSummaryDrilldownLocation, setRenewalsSummaryDrilldownLocation] = useState('');
  const [renewalsSummaryDrilldownFilters, setRenewalsSummaryDrilldownFilters] = useState([]);

  // Upsell / Cross-Sell Qualified Pipeline table drilldown state
  const [upsellCrossSellDrilldownOpen, setUpsellCrossSellDrilldownOpen] = useState(false);
  const [upsellCrossSellDrilldownFilters, setUpsellCrossSellDrilldownFilters] = useState([]);
  const [upsellCrossSellDrilldownTitle, setUpsellCrossSellDrilldownTitle] = useState('Opportunities in Pipeline');

  // Strategic Accounts - Qualified Pipeline drilldown state
  const [strategicAccountsDrilldownOpen, setStrategicAccountsDrilldownOpen] = useState(false);
  const [strategicAccountsDrilldownFilters, setStrategicAccountsDrilldownFilters] = useState([]);
  const [strategicAccountsDrilldownTitle, setStrategicAccountsDrilldownTitle] = useState('Strategic Accounts – Opportunities');

  // Discovery or Later Pipeline Coverage drilldown state
  const [discoveryPipelineDrilldownOpen, setDiscoveryPipelineDrilldownOpen] = useState(false);
  const [discoveryPipelineDrilldownFilters, setDiscoveryPipelineDrilldownFilters] = useState([]);

  // Config State
  const [cardsConfig, setCardsConfig] = useState([]);
  const [forecastOpportunityColumns, setForecastOpportunityColumns] = useState([]);
  const [wonOpportunityColumns, setWonOpportunityColumns] = useState([]);

  const [loadingConfig, setLoadingConfig] = useState(true);

  // Effective columns: prefer columns embedded in individual paper responses (always fresh from
  // the backend), fall back to /config result, then to the built-in default.
  const forecastEffectiveColumns = useMemo(() => {
    const fromPaper =
      salesForecastCurrentQuarterData?.forecastOppsColumns ||
      forecastMovedOutData?.forecastOppsColumns ||
      forecastBestCaseData?.forecastOppsColumns ||
      salesForecastNextQuarterData?.forecastOppsColumns ||
      salesForecastNextQuarterPlus1Data?.forecastOppsColumns;
    const cols = fromPaper || forecastOpportunityColumns;
    return cols && cols.length > 0 ? cols : null;
  }, [
    salesForecastCurrentQuarterData,
    forecastMovedOutData,
    forecastBestCaseData,
    salesForecastNextQuarterData,
    salesForecastNextQuarterPlus1Data,
    forecastOpportunityColumns,
  ]);

  // Pre-build ResizableTable columns from the effective column config
  const forecastResizableColumns = useMemo(
    () => buildForecastColumnsFromConfig(forecastEffectiveColumns || []),
    [forecastEffectiveColumns]
  );

  // Won opps columns: prefer columns embedded in the paper response, fall back to /config result
  const wonEffectiveColumns = useMemo(() => {
    const cols = wonOpportunityColumns;
    return cols && cols.length > 0 ? cols : [];
  }, [wonOpportunityColumns]);

  // Cell value formatter for forecast opportunity rows (raw DB field names)
  const forecastGetCellValue = useCallback((row, field, rowIndex) => {
    if (field === 'rowNum') {
      if (row.label === 'Total' || row.opportunity?.startsWith('Total')) return '-';
      return rowIndex != null ? rowIndex + 1 : (row[field] ?? '');
    }
    if (field === 'Projected_Close_Date_At_Time' || field === 'Projected_Close_Date') {
      const val = row[field] ?? row.Projected_Close_Date ?? row.Projected_Close_Date_At_Time;
      if (!val) return '';
      try { return dateFormatterMomentV3(val); } catch (_) { return ''; }
    }
    if (field === 'Acv_At_Time_T' || field === 'ACV') {
      let val = row[field] ?? row.ACV ?? row.Acv_At_Time_T;
      if (val != null) {
        return d3.format(',.0f')(val / 1000);
      }
      if (row.acvInK != null) {
        return d3.format(',.0f')(row.acvInK);
      }
      return '';
    }
    if (field === 'Days_Since_Qualified' || field === 'Days_at_Stage') {
      const val = row[field];
      return val != null ? d3.format(',')(val) : '';
    }
    // Stage / stage_name_char alias so config works with either field name
    if (field === 'Stage' || field === 'stage_name_char') {
      const val = row[field] ?? row.Stage ?? row.stage_name_char;
      return val != null ? val : '';
    }
    // Executive Sponsor, Forecast Category, Risks, Actions - support both config field names
    if (field === 'Executive_Sponsor') {
      return row.Executive_Sponsor != null ? row.Executive_Sponsor : '';
    }
    if (field === 'Forecast_Category' || field === 'Forecast_Category_At_Time') {
      const val = row[field] ?? row.Forecast_Category ?? row.Forecast_Category_At_Time;
      return val != null ? val : '';
    }
    if (field === 'Risks' || field === 'Risk__c') {
      const val = row[field] ?? row.Risks ?? row.Risk__c;
      return val != null ? val : '';
    }
    if (field === 'Actions' || field === 'Action__c') {
      const val = row[field] ?? row.Actions ?? row.Action__c;
      return val != null ? val : '';
    }
    return row[field] != null ? row[field] : '';
  }, []);

  // Row click handler for ResizableTable forecast opportunity rows
  const handleOppRowClick = useCallback((row) => {
    if (row?.Opp_ID) {
      handleSecondLevelDrilldown('drilldownOppId', row.Opp_ID);
    }
  }, []);

  // Load config on mount
  useEffect(() => {
    setLoadingConfig(true);
    appService.getRegionalSalesReviewConfig()
      .then((data) => {
        if (data && data.message) {
          if (data.message.cardsConfig) {
            setCardsConfig(data.message.cardsConfig);
          }
          if (data.message.forecastOpportunityColumns) {
            setForecastOpportunityColumns(data.message.forecastOpportunityColumns);
          }
          if (data.message.wonOpportunityColumns) {
            setWonOpportunityColumns(data.message.wonOpportunityColumns);
          }
        }
      })
      .catch((err) => {
        console.error("Error loading config:", err);
      })
      .finally(() => {
        setLoadingConfig(false);
      });
  }, []);

  // Reset to 'All' when component mounts
  useEffect(() => {
    setPendingTeam('All');
    setAppliedTeam('All');
    setInitialLoadDone(false);
  }, []);

  // Fetch first-level teams on mount
  useEffect(() => {
    props.getFirstLevelTeams();
  }, []);

  // Set default team when teams API returns (uses manager's team if applicable)
  useEffect(() => {
    if (!initialLoadDone && rsrFirstLevelTeams && rsrFirstLevelTeams.length > 0 && cardsConfig && cardsConfig.length > 0) {
      const defaultTeam = rsrDefaultTeam || 'All';
      setPendingTeam(defaultTeam);
      setAppliedTeam(defaultTeam);
      
      let filters = [];
      if (defaultTeam !== 'All') {
        filters.push({ name: 'team', value: defaultTeam });
      }
      if (appliedCustType !== 'All') {
        filters.push({ name: 'Cust_Type', value: appliedCustType, string: true });
      }
      
      if (cardsConfig.some(c => c.id === 'RSRP_Forecast_Trend')) props.getForecastTrendData(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_Sales_Forecast_Current_Qtr')) props.getSalesForecastCurrentQuarterData(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_Forecast_Moved_Out')) props.getForecastMovedOutData(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_Forecast_Best_Case')) props.getForecastBestCaseData(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_Sales_Forecast_Next_Qtr')) props.getSalesForecastNextQuarterData(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_Sales_Forecast_Next_Qtr_Plus1')) props.getSalesForecastNextQuarterPlus1Data(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_Deal_Progression')) props.getDealProgressionData(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_Deal_Conversion')) props.getDealConversionData(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_Upsell_Cross_Sell') || cardsConfig.some(c => c.id === 'RSRP_Upsell_Cross_Sell_Detail')) props.getUpsellCrossSellData(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_Qualified_Pipeline_Coverage')) props.getQualifiedPipelineCoverageData(filters);

      if (cardsConfig.some(c => c.id === 'RSRP_Renewals_Summary')) props.getRenewalsSummaryData(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_Strategic_Accounts_Qualified_Pipeline')) props.getStrategicAccountsQualifiedPipelineData(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_Discovery_Pipeline_Coverage')) props.getDiscoveryPipelineCoverageData(filters);
      if (cardsConfig.some(c => c.id === 'RSRP_MSSP_Fulfillment')) props.getMsspFulfillmentData(filters);

      setInitialLoadDone(true);
    }
  }, [rsrFirstLevelTeams, rsrDefaultTeam, initialLoadDone, cardsConfig]);

  // Handle team filter change (just updates pending, doesn't fetch)
  const handleTeamChange = (event) => {
    setPendingTeam(event.target.value);
  };

  // Handle Go button click - apply the filter and fetch data
  const handleGo = () => {
    setAppliedTeam(pendingTeam);
    setAppliedCustType(pendingCustType);

    let filters = [];
    if (pendingTeam !== 'All') {
      filters.push({ name: 'team', value: pendingTeam });
    }
    if (pendingCustType !== 'All') {
      filters.push({ name: 'Cust_Type', value: pendingCustType, string: true });
    }
    
    if (cardsConfig.some(c => c.id === 'RSRP_Forecast_Trend')) props.getForecastTrendData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Sales_Forecast_Current_Qtr')) props.getSalesForecastCurrentQuarterData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Forecast_Moved_Out')) props.getForecastMovedOutData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Forecast_Best_Case')) props.getForecastBestCaseData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Sales_Forecast_Next_Qtr')) props.getSalesForecastNextQuarterData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Sales_Forecast_Next_Qtr_Plus1')) props.getSalesForecastNextQuarterPlus1Data(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Deal_Progression')) props.getDealProgressionData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Deal_Conversion')) props.getDealConversionData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Upsell_Cross_Sell') || cardsConfig.some(c => c.id === 'RSRP_Upsell_Cross_Sell_Detail')) props.getUpsellCrossSellData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Qualified_Pipeline_Coverage')) props.getQualifiedPipelineCoverageData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Renewals_Summary')) props.getRenewalsSummaryData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Strategic_Accounts_Qualified_Pipeline')) props.getStrategicAccountsQualifiedPipelineData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_Discovery_Pipeline_Coverage')) props.getDiscoveryPipelineCoverageData(filters);
    if (cardsConfig.some(c => c.id === 'RSRP_MSSP_Fulfillment')) props.getMsspFulfillmentData(filters);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'elapsed':
        return classes.elapsedBadge;
      case 'current':
        return classes.currentBadge;
      default:
        return classes.futureBadge;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'elapsed':
        return 'Completed';
      case 'current':
        return 'Current';
      default:
        return 'Future';
    }
  };

  // Drilldown handlers - Directly go to second-level drilldown with oppId
  const handleChangeLocation = (location, row, tableType) => {
    if (location === "drilldownOnGraph") {
      // Don't allow drilldown on Total, Quota, or Other Opps rows
      if (row.label === 'Total' || row.label === 'Quota' || row.label === 'Other Opps') {
        return;
      }

      // Get oppId from row and directly show second-level drilldown (opportunity change events)
      const oppId = row.Opp_ID || row.oppId;
      if (oppId) {
        handleSecondLevelDrilldown("drilldownOppId", oppId);
      } else {
        // If no oppId is available, log a warning (could also show a user-friendly message)
        console.warn('No oppId found in row data for drilldown:', row);
      }
    } else {
      setSecondLevelLocation('');
      props.clearExpectedAcvData();
    }
  };

  const handleChangeLocationBack = () => {
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  const handleSecondLevelDrilldown = (location, oppId) => {
    setSecondLevelLocation(location);
    props.getOpenOppData(oppId);
  };

  // Handler for ForecastTrend chart bar click
  const handleForecastTrendDrilldown = ({ type, quarter, weekEnding }) => {
    const filters = [
      { name: 'quarter', value: quarter },
      { name: 'weekEnding', value: weekEnding },
    ];
    // Include team filter if selected
    if (appliedTeam && appliedTeam !== 'All') {
      filters.push({ name: 'team', value: appliedTeam });
    }
    if (appliedCustType && appliedCustType !== 'All') {
      filters.push({ name: 'Cust_Type', value: appliedCustType, string: true });
    }

    setForecastTrendDrilldownLocation(type);

    // Build display filters - include team if selected
    const displayFilters = [
      { title: 'Quarter', value: quarter },
      { title: 'Week Ending', value: weekEnding },
    ];
    if (appliedTeam && appliedTeam !== 'All') {
      displayFilters.push({ title: 'Team', value: appliedTeam });
    }
    if (appliedCustType && appliedCustType !== 'All') {
      displayFilters.push({ title: 'Customer Type', value: appliedCustType });
    }
    setForecastTrendDrilldownFilters(displayFilters);

    if (type === 'won') {
      props.getForecastTrendWonDrilldown(filters);
    } else if (type === 'mostLikely') {
      props.getForecastTrendMostLikelyDrilldown(filters);
    }
  };

  // Handler to close forecast trend drilldown
  const handleForecastTrendDrilldownBack = () => {
    setForecastTrendDrilldownLocation('');
    setForecastTrendDrilldownFilters([]);
    props.clearForecastTrendDrilldown();
  };

  // Step back from second level to first level drilldown (for forecast trend)
  const handleForecastTrendStepBack = () => {
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  // Handler for qualified pipeline coverage drilldown
  const handleQualifiedPipelineDrilldown = ({ quarter }) => {
    const filters = [
      { name: 'quarter', value: quarter },
    ];
    // Include team filter if selected
    if (appliedTeam && appliedTeam !== 'All') {
      filters.push({ name: 'team', value: appliedTeam });
    }
    if (appliedCustType && appliedCustType !== 'All') {
      filters.push({ name: 'Cust_Type', value: appliedCustType, string: true });
    }

    setQualifiedPipelineDrilldownOpen(true);

    // Build display filters
    const displayFilters = [
      { title: 'Quarter', value: quarter },
    ];
    if (appliedTeam && appliedTeam !== 'All') {
      displayFilters.push({ title: 'Team', value: appliedTeam });
    }
    if (appliedCustType && appliedCustType !== 'All') {
      displayFilters.push({ title: 'Customer Type', value: appliedCustType });
    }
    setQualifiedPipelineDrilldownFilters(displayFilters);

    props.getQualifiedPipelineCoverageDrilldown(filters);
  };

  // Handler to close qualified pipeline coverage drilldown
  const handleQualifiedPipelineDrilldownBack = () => {
    setQualifiedPipelineDrilldownOpen(false);
    setQualifiedPipelineDrilldownFilters([]);
    setSecondLevelLocation('');
    props.clearQualifiedPipelineCoverageDrilldown();
    props.clearExpectedAcvData();
  };

  // Step back from second level to first level drilldown (for qualified pipeline)
  const handleQualifiedPipelineStepBack = () => {
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  // Handler for discovery pipeline coverage drilldown
  const handleDiscoveryPipelineDrilldown = ({ quarter }) => {
    const filters = [
      { name: 'quarter', value: quarter },
    ];
    // Include team filter if selected
    if (appliedTeam && appliedTeam !== 'All') {
      filters.push({ name: 'team', value: appliedTeam });
    }
    if (appliedCustType && appliedCustType !== 'All') {
      filters.push({ name: 'Cust_Type', value: appliedCustType, string: true });
    }

    setDiscoveryPipelineDrilldownOpen(true);

    // Build display filters
    const displayFilters = [
      { title: 'Quarter', value: quarter },
    ];
    if (appliedTeam && appliedTeam !== 'All') {
      displayFilters.push({ title: 'Team', value: appliedTeam });
    }
    if (appliedCustType && appliedCustType !== 'All') {
      displayFilters.push({ title: 'Customer Type', value: appliedCustType });
    }
    setDiscoveryPipelineDrilldownFilters(displayFilters);

    props.getDiscoveryPipelineCoverageDrilldown(filters);
  };

  // Handler to close discovery pipeline coverage drilldown
  const handleDiscoveryPipelineDrilldownBack = () => {
    setDiscoveryPipelineDrilldownOpen(false);
    setDiscoveryPipelineDrilldownFilters([]);
    setSecondLevelLocation('');
    props.clearDiscoveryPipelineCoverageDrilldown();
    props.clearExpectedAcvData();
  };

  // Step back from second level to first level drilldown (for discovery pipeline)
  const handleDiscoveryPipelineStepBack = () => {
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  // Handler for Renewals Summary Drilldown
  const handleRenewalsSummaryDrilldown = ({ type, quarter, status }) => {
    setRenewalsSummaryDrilldownLocation(type || 'table');
    
    const filters = [];

    const allQuarters = props.renewalsSummaryData?.renewalDueDateFiscalQuarter || [];
    const allStatuses = props.renewalsSummaryData?.renewalStatusConfig?.map(c => c.Renewal_Status) || [];

    // Include team filter if selected
    if (appliedTeam && appliedTeam !== 'All') {
      filters.push({ name: 'team', value: appliedTeam });
    }
    if (appliedCustType && appliedCustType !== 'All') {
      filters.push({ name: 'Cust_Type', value: appliedCustType, string: true });
    }

    if (quarter) {
        if (quarter === 'Total') {
            filters.push({ name: 'Renewal_Due_Date_Fiscal_Quarter', value: allQuarters });
        } else {
            filters.push({ name: 'Renewal_Due_Date_Fiscal_Quarter', value: [quarter] });
        }
    }
    if (status) {
        if (status === 'Total') {
            filters.push({ name: 'Renewal_Status', value: allStatuses });
        } else {
            filters.push({ name: 'Renewal_Status', value: [status] });
        }
    }
    
    const displayFilters = [];
    if (appliedTeam && appliedTeam !== 'All') {
      displayFilters.push({ title: 'Team', value: appliedTeam });
    }
    if (appliedCustType && appliedCustType !== 'All') {
      displayFilters.push({ title: 'Customer Type', value: appliedCustType });
    }
    if (quarter) {
      displayFilters.push({ title: 'Renewal Due Date Fiscal Quarter', value: quarter === 'Total' ? 'All' : quarter });
    }
    if (status) {
      displayFilters.push({ title: 'Renewal Status', value: status === 'Total' ? 'All' : status });
    }
    setRenewalsSummaryDrilldownFilters(displayFilters);
    props.getRenewalsSummaryDrilldownData(filters);
  };

  const handleRenewalsSummaryDrilldownBack = () => {
    setRenewalsSummaryDrilldownLocation('');
    setRenewalsSummaryDrilldownFilters([]);
    props.clearRenewalsSummaryDrilldown();
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  // Handler for Upsell / Cross-Sell Qualified Pipeline table drilldown (same pattern as Pipeline Mix by Customer Type)
  const handleUpsellCrossSellTableDrilldown = (location, data, type, quarter) => {
    if (location !== 'drilldownOnGraph' || !data || !type || !quarter) return;
    const custTypeItem = upsellCrossSellData?.pipelineMixByCustType;
    if (!custTypeItem || !custTypeItem.data) return;

    const tableData = custTypeItem.data;
    const uniqueQuarters = [...new Set(tableData.map((r) => r[quarter]).filter(Boolean))];
    const uniqueRows = [...new Set(tableData.map((r) => r[type]).filter(Boolean))];

    const quarterValue = data.cell !== 'Total' ? [data.cell] : uniqueQuarters;
    const typeValue = data.row !== 'Total' ? [data.row] : uniqueRows;

    const filters = [
      { name: quarter, value: quarterValue, string: true },
      { name: type, value: typeValue, string: true },
    ];
    if (appliedTeam && appliedTeam !== 'All') {
      filters.push({ name: 'team', value: appliedTeam });
    }
    if (appliedCustType && appliedCustType !== 'All') {
      filters.push({ name: 'Cust_Type', value: appliedCustType, string: true });
    }

    const displayFilters = [
      { title: quarter === 'projected_close_fiscal_quarter' ? 'Projected Close Fiscal Quarter' : quarter, value: data.cell !== 'Total' ? data.cell : 'All' },
      { title: 'Customer Type', value: data.row !== 'Total' ? data.row : 'All' },
    ];
    if (appliedTeam && appliedTeam !== 'All') {
      displayFilters.push({ title: 'Team', value: appliedTeam });
    }
    if (appliedCustType && appliedCustType !== 'All') {
      displayFilters.push({ title: 'Customer Type', value: appliedCustType });
    }

    setUpsellCrossSellDrilldownTitle('Upsell / Cross-Sell Qualified Pipeline – Opportunities');
    setUpsellCrossSellDrilldownFilters(displayFilters);
    setUpsellCrossSellDrilldownOpen(true);
    props.getUpsellCrossSellDrilldown(filters);
  };

  const handleUpsellCrossSellDrilldownBack = () => {
    setUpsellCrossSellDrilldownOpen(false);
    setUpsellCrossSellDrilldownFilters([]);
    setSecondLevelLocation('');
    props.clearUpsellCrossSellDrilldown();
    props.clearExpectedAcvData();
  };

  const handleUpsellCrossSellStepBack = () => {
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  // Strategic Accounts - Qualified Pipeline drilldown handlers
  const handleStrategicAccountsDrilldown = (location, data, type, quarter) => {
    if (location !== 'drilldownOnGraph' || !data || !type || !quarter) return;
    const tableData = strategicAccountsQualifiedPipelineData?.data || [];
    const uniqueQuarters = [...new Set(tableData.map((r) => r[quarter]).filter(Boolean))];
    const uniqueAccounts = [...new Set(tableData.map((r) => r[type]).filter(Boolean))];

    const quarterValue = data.cell !== 'Total' ? [data.cell] : uniqueQuarters;
    const accountValue = data.row !== 'Total' ? [data.row] : uniqueAccounts;

    const filters = [
      { name: quarter, value: quarterValue, string: true },
      { name: type, value: accountValue, string: true },
    ];

    const displayFilters = [
      { title: 'Projected Close Quarter', value: data.cell !== 'Total' ? data.cell : 'All' },
      { title: 'Account Name', value: data.row !== 'Total' ? data.row : 'All' },
    ];
    if (appliedTeam && appliedTeam !== 'All') {
      filters.push({ name: 'team', value: appliedTeam });
      displayFilters.push({ title: 'Team', value: appliedTeam });
    }
    if (appliedCustType && appliedCustType !== 'All') {
      filters.push({ name: 'Cust_Type', value: appliedCustType, string: true });
      displayFilters.push({ title: 'Customer Type', value: appliedCustType });
    }

    setStrategicAccountsDrilldownTitle('Strategic Accounts – Qualified Pipeline – Opportunities');
    setStrategicAccountsDrilldownFilters(displayFilters);
    setStrategicAccountsDrilldownOpen(true);
    props.getStrategicAccountsQualifiedPipelineDrilldown(filters);
  };

  const handleStrategicAccountsDrilldownBack = () => {
    setStrategicAccountsDrilldownOpen(false);
    setStrategicAccountsDrilldownFilters([]);
    setSecondLevelLocation('');
    props.clearStrategicAccountsQualifiedPipelineDrilldown();
    props.clearExpectedAcvData();
  };

  const handleStrategicAccountsStepBack = () => {
    setSecondLevelLocation('');
    props.clearExpectedAcvData();
  };

  const isForecastTrendLoading = forecastTrendData === "";
  const isSalesForecastCurrentQuarterLoading = salesForecastCurrentQuarterData === "";
  const isForecastMovedOutLoading = forecastMovedOutData === "";
  const isForecastBestCaseLoading = forecastBestCaseData === "";
  const isSalesForecastNextQuarterLoading = salesForecastNextQuarterData === "";
  const isQualifiedPipelineCoverageLoading = qualifiedPipelineCoverageData === "";
  const isSalesForecastNextQuarterPlus1Loading = salesForecastNextQuarterPlus1Data === "";
  const isDealProgressionLoading = dealProgressionData === "";
  const isDealConversionLoading = dealConversionData === "";
  const isUpsellCrossSellLoading = upsellCrossSellData === "";
  const isRenewalsSummaryLoading = renewalsSummaryData === "";
  const isDiscoveryPipelineCoverageLoading = discoveryPipelineCoverageData === "";

  // Format "As of MMM. DD, YYYY" from a date string (e.g., "2026-01-24")
  const formatAsOfDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `As of ${monthNames[date.getMonth()]}. ${date.getDate()}, ${date.getFullYear()}`;
  };

  const asOfDateLabel = formatAsOfDate(forecastTrendData?.mostRecentWeekEnding);
  // Helper function to format date to MM/DD/YYYY using existing customFunctions
  const formatDateToMMDDYYYY = useCallback((dateString) => {
    if (!dateString || dateString === '') return '';
    try {
      // Use dateFormatterMomentV3 which returns MM/DD/YYYY
      return dateFormatterMomentV3(dateString);
    } catch (error) {
      return '';
    }
  }, []);

  // Helper function to format cell values
  const getCellValue = useCallback((row, field, rowIndex) => {
    // Always show sequential row numbers regardless of sort order
    if (field === 'rowNum') {
      // For Total/Quota/Other Opps rows, return null or original value
      if (row.label === 'Total' || row.label === 'Quota' || row.label === 'Other Opps') {
        return row[field] != null ? row[field] : null;
      }
      // For regular rows, use rowIndex to show sequential numbers
      return rowIndex != null ? rowIndex + 1 : (row[field] != null ? row[field] : '');
    }
    // Legacy field names
    if (field === 'projectedCloseDate') {
      return formatDateToMMDDYYYY(row[field]);
    }
    if (field === 'acvInK' && row[field] != null) {
      return d3.format(",.0f")(row[field]);
    }
    if (field === 'ageSinceQualDays' && row[field] != null) {
      return d3.format(",")(row[field]);
    }
    if (field === 'daysInStage' && row[field] != null) {
      return d3.format(",")(row[field]);
    }
    // LKP_DRILLDOWN_CONFIG field names (also handles default fallback field names)
    if (field === 'Projected_Close_Date' || field === 'Projected_Close_Date_At_Time') {
      return formatDateToMMDDYYYY(row[field]);
    }
    if ((field === 'ACV' || field === 'Acv_At_Time_T') && row[field] != null) {
      return d3.format(",.0f")(row[field] / 1000);
    }
    if (field === 'Days_Since_Qualified' && row[field] != null) {
      return d3.format(",")(row[field]);
    }
    if (field === 'Days_at_Stage' && row[field] != null) {
      return d3.format(",")(row[field]);
    }
    if (field === 'Executive_Sponsor' || field === 'executiveSponsor') {
      return row.Executive_Sponsor != null ? row.Executive_Sponsor : (row.executiveSponsor != null ? row.executiveSponsor : '');
    }
    if (row[field] == null || row[field] === '') {
      return '';
    }
    return row[field];
  }, [formatDateToMMDDYYYY]);

  // Helper function to get cell styles
  const getCellStyle = useCallback((row, field) => {
    if (row.label === 'Total') {
      return { fontWeight: 600, backgroundColor: '#f5f5f5' };
    }
    if (row.isNewThisWeek) {
      return { backgroundColor: '#c8e6c9' };
    }
    return {};
  }, []);

  // Prepare data for Forecast Best Case table
  const forecastBestCaseRows = useMemo(() => {
    const rows = transformOpportunityDataToRows(
      forecastBestCaseData?.bestCaseOpps,
      forecastBestCaseData?.bestCaseOpps?.length > 0,
      forecastBestCaseData?.bestCaseOpps?.length > 0
        ? {
            totalCount: forecastBestCaseData.totalCount,
            totalACVInK: forecastBestCaseData.totalACVInK
          }
        : null
    );
    return rows;
  }, [forecastBestCaseData]);

  // Prepare data for Forecast Moved Out table
  const forecastMovedOutRows = useMemo(() => {
    const rows = transformOpportunityDataToRows(
      forecastMovedOutData?.movedOutOpps,
      forecastMovedOutData?.movedOutOpps?.length > 0,
      forecastMovedOutData?.movedOutOpps?.length > 0
        ? {
            totalCount: forecastMovedOutData.totalCount,
            totalACVInK: forecastMovedOutData.totalACVInK
          }
        : null
    );
    return rows;
  }, [forecastMovedOutData]);


  // Prepare columns for Forecast Best Case table (after rows are calculated)
  const forecastBestCaseColumns = useMemo(() => {
    const dynamic = buildColumnsFromConfig(
      forecastOpportunityColumns,
      forecastBestCaseRows,
      getCellValue,
      props.user?.Currency || ''
    );
    return dynamic || createOpportunityTableColumns(forecastBestCaseRows, getCellValue, props.user?.Currency || '');
  }, [forecastBestCaseRows, getCellValue, props.user, forecastOpportunityColumns]);

  const matchedDashboardItem = props.helperTextCharts?.find(
    (e) =>
      e.dashboard_id === "SG_Dashboard_Regional_Sales_Review_Package_Custom_Scorecards"
  );

  // console.log("Current Week Number:", forecastTrendData?.currentWeekNumber);
  // console.log("Current Quarter:",forecastTrendData?.currentQuarter);
  

  return (
    <div className={classes.root}>
      <AddHeaderDescriptionWithHelperText
        header={
          matchedDashboardItem?.Chart_Name || "Regional Sales Review Package"
        }
        description={
          matchedDashboardItem?.Description || "Regional Sales Review Package"
        }
        helperTextCharts={props.helperTextCharts}
      />
      {/* Team Filter */}
      <Box className={classes.filterWrapper}>
        <Paper className={classes.filterContainer} elevation={0}>
          <FormControl className={classes.formControl}>
            <InputLabel
              className={classes.filterLabel}
              id="team-filter-label"
              style={{
                color: pendingTeam !== '' && pendingTeam !== 'All' ? '#4472c4' : '#000',
              }}
            >
              Team:
            </InputLabel>
            <Select
              labelId="team-filter-label"
              id="team-filter"
              value={pendingTeam}
              onChange={handleTeamChange}
              label="Team:"
            >
              <MenuItem value="All">
                All
              </MenuItem>
              {rsrFirstLevelTeams && rsrFirstLevelTeams.map((team) => (
                <MenuItem key={team} value={team}>
                  {team}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
        <Button
          color="primary"
          variant="contained"
          onClick={handleGo}
          className={classes.goButton}
        >
          GO
        </Button>
      </Box>

      {/* PPTX Export Button */}
      <RegionalSalesReviewPackagePPTX
        dashboardTitle={matchedDashboardItem?.Chart_Name || "Regional Sales Review Package"}
        fileName={matchedDashboardItem?.Chart_Name ? matchedDashboardItem.Chart_Name.replace(/\s+/g, '_') : "Regional_Sales_Review_Package"}
        currentQuarter={forecastTrendData?.currentQuarter}
        currentWeekNumber={forecastTrendData?.currentWeekNumber}
      />

      {/* Dynamic Rendering Loop */}
      {loadingConfig ? (
        <Box mt={4} display="flex" justifyContent="center">
          <Typography variant="body1">Loading Configuration...</Typography>
        </Box>
      ) : (
        cardsConfig && cardsConfig.length > 0 && cardsConfig
          .sort((a, b) => a.sequence - b.sequence)
          .map((cardConfig) => {
            const CardComponent = getRegionalCard(cardConfig.id);
            if (!CardComponent) return null;

            let specificProps = {};

            switch (cardConfig.id) {
              case "RSRP_Forecast_Trend":
                specificProps = {
  forecastTrendData,
  isForecastTrendLoading,
  handleForecastTrendDrilldown,
                };
                break;
              case "RSRP_Qualified_Pipeline_Coverage":
                specificProps = {
                  qualifiedPipelineCoverageData,
  isQualifiedPipelineCoverageLoading,
  handleQualifiedPipelineDrilldown,
                };
                break;
              case "RSRP_Discovery_Pipeline_Coverage":
                specificProps = {
                  discoveryPipelineCoverageData,
                  isDiscoveryPipelineCoverageLoading,
                  handleDiscoveryPipelineDrilldown,
                };
                break;
              case "RSRP_Sales_Forecast_Current_Qtr":
                specificProps = {
                  salesForecastCurrentQuarterData,
                  isSalesForecastCurrentQuarterLoading,
                  asOfDateLabel,
                  handleChangeLocation,
                  forecastOpportunityColumns: forecastEffectiveColumns,
                  wonOpportunityColumns: wonEffectiveColumns,
                };
                break;
              case "RSRP_Forecast_Moved_Out":
                specificProps = {
                  isForecastMovedOutLoading,
                  forecastMovedOutData,
                  forecastMovedOutRows,
                  forecastResizableColumns,
                  forecastGetCellValue,
                  handleOppRowClick,
                  createSummaryTableColumns,
                  currencySymbol: props.user?.Currency || "",
                };
                break;
              case "RSRP_Forecast_Best_Case":
                specificProps = {
                  isForecastBestCaseLoading,
                  forecastBestCaseData,
                  forecastBestCaseColumns,
                  forecastBestCaseRows,
                  getCellValue,
                  getCellStyle,
                  handleChangeLocation,
                };
                break;
              case "RSRP_Sales_Forecast_Next_Qtr":
                specificProps = {
                  salesForecastNextQuarterData,
                  isSalesForecastNextQuarterLoading,
                  forecastResizableColumns,
                  forecastGetCellValue,
                  handleOppRowClick,
                  createSummaryTableColumns,
                };
                break;
              case "RSRP_Sales_Forecast_Next_Qtr_Plus1":
                specificProps = {
                  salesForecastNextQuarterPlus1Data,
                  isSalesForecastNextQuarterPlus1Loading,
                  forecastResizableColumns,
                  forecastGetCellValue,
                  handleOppRowClick,
                  createSummaryTableColumns,
                };
                break;
              case "RSRP_Strategic_Accounts_Qualified_Pipeline":
                specificProps = {
                  strategicAccountsQualifiedPipelineData,
                  createSummaryTableColumns,
                  handleChangeLocation: handleStrategicAccountsDrilldown,
                };
                break;
              case "RSRP_MSSP_Fulfillment":
                specificProps = {
                  msspFulfillmentData,
                  handleOppRowClick,
                  buildColumnsFromConfig,
                  createOpportunityTableColumns,
                  getCellValue,
                };
                break;
              case "RSRP_Deal_Progression":
                specificProps = {
                    dealProgressionData,
  isDealProgressionLoading
                };
                break;
              case "RSRP_Deal_Conversion":
                specificProps = {
                    dealConversionData,
  isDealConversionLoading,
  company: props.company,
                };
                break;
              case "RSRP_Win_Loss_Analysis":
                specificProps = {
                      createWinLossTableColumns,
  handleChangeLocation,
                };
                break;
              case "RSRP_Renewals_Detail":
                specificProps = {
                      createRenewalsTableColumns,
  handleChangeLocation,
                };
                break;
              case "RSRP_Upsell_Cross_Sell":
                specificProps = {
                  upsellCrossSellData,
  isUpsellCrossSellLoading,
  appliedTeam,
                };
                break;
                case "RSRP_Upsell_Cross_Sell_Detail":
                specificProps = {
                  upsellCrossSellData,
                  Value_Label: props.Value_Label,
                  handleChangeLocation: handleUpsellCrossSellTableDrilldown,
                };
                break;
              case "RSRP_Renewals_Summary":
                specificProps = {
                  renewalsSummaryData,
                  isRenewalsSummaryLoading,
                  handleRenewalsSummaryDrilldown,
                };
                break;
              default:
                break;
            }

            const renderedCard = (
              <Box mt={4} key={cardConfig.id}>
                  <CardComponent
                    classes={classes}
                    paperTitle={cardConfig.displayTitle}
                    currencySymbol={props.user?.Currency || ""}
                    {...specificProps}
                  />
              </Box>
            );

            // Handle Drilldowns that need to be injected after specific cards
            if (cardConfig.id === "RSRP_Forecast_Trend") {
               return (
                 <React.Fragment key={cardConfig.id}>
                   {renderedCard}
                    {/* Forecast Trend Drilldown - First Level */}
                    {(forecastTrendDrilldownLocation === 'won' || forecastTrendDrilldownLocation === 'mostLikely') && secondLevelLocation !== 'drilldownOppId' && (
                      <Grid
                        item
                        xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
                      >
                        <SingleLevelDrilldown
                          header={forecastTrendDrilldownLocation === 'won'
                            ? 'Won Opportunities'
                            : 'Most Likely / Commit Opportunities'}
                          back={handleForecastTrendDrilldownBack}
                          filters={forecastTrendDrilldownFilters}
                        >
                          <DrilldownWithSLD
                            header={forecastTrendDrilldownLocation === 'won'
                              ? 'Closed-Won Opportunities'
                              : 'Pipeline Opportunities (Most Likely / Commit)'}
                            body={props.forecastTrendDrilldownData}
                            company={props.company}
                            Value_Label={props.Value_Label}
                            handleSecondLevelDrilldown={handleSecondLevelDrilldown}
                          />
                        </SingleLevelDrilldown>
                      </Grid>
                    )}

                    {/* Forecast Trend Drilldown - Second Level (Opportunity Change Events) */}
                    {(forecastTrendDrilldownLocation === 'won' || forecastTrendDrilldownLocation === 'mostLikely') && secondLevelLocation === 'drilldownOppId' && (
                      <Grid
                        item
                        xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
                      >
                        <SingleLevelDrilldown
                          header={"Opportunity Change Events"}
                          back={handleForecastTrendDrilldownBack}
                          stepBack={handleForecastTrendStepBack}
                          filters={[]}
                        >
                          <ExpectedAcvPage
                            body={props.ExpectedAcvData}
                            Value_Label={props.Value_Label}
                          />
                        </SingleLevelDrilldown>
                      </Grid>
                    )}

                    
                 </React.Fragment>
               );
            }
            else if (cardConfig.id === "RSRP_Discovery_Pipeline_Coverage") {
                return (
                  <React.Fragment key={cardConfig.id}>
                    {renderedCard}
                    {/* Discovery Pipeline Coverage Drilldown - First Level */}
                    {discoveryPipelineDrilldownOpen && secondLevelLocation !== 'drilldownOppId' && (
                      <Grid
                        item
                        xs={12}
                        style={{ position: "absolute", width: "100%", marginTop: "30px" }}
                      >
                        <SingleLevelDrilldown
                          header={'Discovery or Later Pipeline Opportunities'}
                          back={handleDiscoveryPipelineDrilldownBack}
                          filters={discoveryPipelineDrilldownFilters}
                        >
                          <DrilldownWithSLD
                            header={'Open Discovery or Later Opportunities'}
                            body={props.discoveryPipelineCoverageDrilldownData}
                            company={props.company}
                            Value_Label={props.Value_Label}
                            handleSecondLevelDrilldown={handleSecondLevelDrilldown}
                          />
                        </SingleLevelDrilldown>
                      </Grid>
                    )}

                    {/* Discovery Pipeline Coverage Drilldown - Second Level (Opportunity Change Events) */}
                    {discoveryPipelineDrilldownOpen && secondLevelLocation === 'drilldownOppId' && (
                      <Grid
                        item
                        xs={12}
                        style={{ position: "absolute", width: "100%", marginTop: "30px" }}
                      >
                        <SingleLevelDrilldown
                          header={"Opportunity Change Events"}
                          back={handleDiscoveryPipelineDrilldownBack}
                          stepBack={handleDiscoveryPipelineStepBack}
                          filters={[]}
                        >
                          <ExpectedAcvPage
                            body={props.ExpectedAcvData}
                            Value_Label={props.Value_Label}
                          />
                        </SingleLevelDrilldown>
                      </Grid>
                    )}
                  </React.Fragment>
                );
              }
            else if (cardConfig.id === "RSRP_Qualified_Pipeline_Coverage") {
               return (
                 <React.Fragment key={cardConfig.id}>
                   {renderedCard}
              {/* Qualified Pipeline Coverage Drilldown - First Level */}
      {qualifiedPipelineDrilldownOpen && secondLevelLocation !== 'drilldownOppId' && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={'Qualified Pipeline Opportunities'}
            back={handleQualifiedPipelineDrilldownBack}
            filters={qualifiedPipelineDrilldownFilters}
          >
            <DrilldownWithSLD
              header={'Open Qualified Opportunities'}
              body={props.qualifiedPipelineCoverageDrilldownData}
              company={props.company}
              Value_Label={props.Value_Label}
              handleSecondLevelDrilldown={handleSecondLevelDrilldown}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}

      {/* Qualified Pipeline Coverage Drilldown - Second Level (Opportunity Change Events) */}
      {qualifiedPipelineDrilldownOpen && secondLevelLocation === 'drilldownOppId' && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={"Opportunity Change Events"}
            back={handleQualifiedPipelineDrilldownBack}
            stepBack={handleQualifiedPipelineStepBack}
            filters={[]}
          >
            <ExpectedAcvPage
              body={props.ExpectedAcvData}
              Value_Label={props.Value_Label}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}
                </React.Fragment>
              );
            }
            else if (cardConfig.id === "RSRP_Renewals_Summary") {
              return (
                <React.Fragment key={cardConfig.id}>
                  {renderedCard}
                  {/* Renewals Summary Drilldown - First Level */}
                  {renewalsSummaryDrilldownLocation && secondLevelLocation !== 'drilldownOppId' && (
                    <Grid
                      item
                      xs={12}
                      style={{ position: "absolute", width: "100%", marginTop: "30px" }}
                    >
                      <SingleLevelDrilldown
                        header={'Renewals Opportunities'}
                        back={handleRenewalsSummaryDrilldownBack}
                        filters={renewalsSummaryDrilldownFilters}
                      >
                        <DrilldownWithSLD
                          header={'Renewals Opportunities'}
                          body={props.renewalsSummaryDrilldownData}
                          company={props.company}
                          Value_Label={props.Value_Label}
                          handleSecondLevelDrilldown={handleSecondLevelDrilldown}
                        />
                      </SingleLevelDrilldown>
                    </Grid>
                  )}

                  {/* Renewals Summary Drilldown - Second Level (Opportunity Change Events) */}
                  {renewalsSummaryDrilldownLocation && secondLevelLocation === 'drilldownOppId' && (
                    <Grid
                      item
                      xs={12}
                      style={{ position: "absolute", width: "100%", marginTop: "30px" }}
                    >
                      <SingleLevelDrilldown
                        header={"Opportunity Change Events"}
                        back={handleRenewalsSummaryDrilldownBack}
                        stepBack={() => {
                          setSecondLevelLocation('');
                          props.clearExpectedAcvData();
                        }}
                        filters={[]}
                      >
                        <ExpectedAcvPage
                          body={props.ExpectedAcvData}
                          Value_Label={props.Value_Label}
                        />
                      </SingleLevelDrilldown>
                    </Grid>
                  )}
                </React.Fragment>
              );
            }
            else if (cardConfig.id === "RSRP_Upsell_Cross_Sell_Detail") {
              return (
                <React.Fragment key={cardConfig.id}>
                  {renderedCard}
                  {/* Upsell / Cross-Sell Qualified Pipeline Drilldown - First Level */}
                  {upsellCrossSellDrilldownOpen && secondLevelLocation !== 'drilldownOppId' && (
                    <Grid
                      item
                      xs={12}
                      style={{ position: "absolute", width: "100%", marginTop: "30px" }}
                    >
                      <SingleLevelDrilldown
                        header={upsellCrossSellDrilldownTitle}
                        back={handleUpsellCrossSellDrilldownBack}
                        filters={upsellCrossSellDrilldownFilters}
                      >
                        <Drilldown
                          header={upsellCrossSellDrilldownTitle}
                          body={props.upsellCrossSellDrilldownData}
                          company={props.company}
                          Value_Label={props.Value_Label}
                          handleSecondLevelDrilldown={handleSecondLevelDrilldown}
                        />
                      </SingleLevelDrilldown>
                    </Grid>
                  )}

                  {/* Upsell / Cross-Sell Drilldown - Second Level (Opportunity Change Events) */}
                  {upsellCrossSellDrilldownOpen && secondLevelLocation === 'drilldownOppId' && (
                    <Grid
                      item
                      xs={12}
                      style={{ position: "absolute", width: "100%", marginTop: "30px" }}
                    >
                      <SingleLevelDrilldown
                        header={"Opportunity Change Events"}
                        back={handleUpsellCrossSellDrilldownBack}
                        stepBack={handleUpsellCrossSellStepBack}
                        filters={[]}
                      >
                        <ExpectedAcvPage
                          body={props.ExpectedAcvData}
                          Value_Label={props.Value_Label}
                        />
                      </SingleLevelDrilldown>
                    </Grid>
                  )}
                </React.Fragment>
              );
            }
            else if (cardConfig.id === "RSRP_Strategic_Accounts_Qualified_Pipeline") {
              return (
                <React.Fragment key={cardConfig.id}>
                  {renderedCard}

                  {/* Strategic Accounts - Qualified Pipeline Drilldown - First Level */}
                  {strategicAccountsDrilldownOpen && secondLevelLocation !== 'drilldownOppId' && (
                    <Grid
                      item
                      xs={12}
                      style={{ position: "absolute", width: "100%", marginTop: "30px" }}
                    >
                      <SingleLevelDrilldown
                        header={strategicAccountsDrilldownTitle}
                        back={handleStrategicAccountsDrilldownBack}
                        filters={strategicAccountsDrilldownFilters}
                      >
                        <Drilldown
                          header={strategicAccountsDrilldownTitle}
                          body={props.strategicAccountsQualifiedPipelineDrilldownData}
                          company={props.company}
                          Value_Label={props.Value_Label}
                          handleSecondLevelDrilldown={handleSecondLevelDrilldown}
                        />
                      </SingleLevelDrilldown>
                    </Grid>
                  )}

                  {/* Strategic Accounts - Qualified Pipeline Drilldown - Second Level */}
                  {strategicAccountsDrilldownOpen && secondLevelLocation === 'drilldownOppId' && (
                    <Grid
                      item
                      xs={12}
                      style={{ position: "absolute", width: "100%", marginTop: "30px" }}
                    >
                      <SingleLevelDrilldown
                        header={"Opportunity Change Events"}
                        back={handleStrategicAccountsDrilldownBack}
                        stepBack={handleStrategicAccountsStepBack}
                        filters={[]}
                      >
                        <ExpectedAcvPage
                          body={props.ExpectedAcvData}
                          Value_Label={props.Value_Label}
                        />
                      </SingleLevelDrilldown>
                    </Grid>
                  )}
                </React.Fragment>
              );
            }
            else {
              return (
                <React.Fragment key={cardConfig.id}>
                  {renderedCard}
                </React.Fragment>
              );
            }
          })
      )}

      {/* Second Level Drilldown (for other charts) - Rendered once outside the loop */}
      {secondLevelLocation === "drilldownOppId" && !forecastTrendDrilldownLocation && !qualifiedPipelineDrilldownOpen && !discoveryPipelineDrilldownOpen && !renewalsSummaryDrilldownLocation && !upsellCrossSellDrilldownOpen && !strategicAccountsDrilldownOpen && (
        <Grid
          item
          xs={12}
          style={{ position: "absolute", width: "100%", marginTop: "30px" }}
        >
          <SingleLevelDrilldown
            header={"Opportunity Change Events"}
            back={handleChangeLocationBack}
            filters={[]}
          >
            <ExpectedAcvPage
              body={props.ExpectedAcvData}
              Value_Label={props.Value_Label}
            />
          </SingleLevelDrilldown>
        </Grid>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const { user, forecastTrendData, forecastTrendDrilldownData, salesForecastCurrentQuarterData, forecastMovedOutData, forecastBestCaseData, salesForecastNextQuarterData, salesForecastNextQuarterPlus1Data, dealProgressionData, dealConversionData, upsellCrossSellData, qualifiedPipelineCoverageData, qualifiedPipelineCoverageDrilldownData, discoveryPipelineCoverageData, discoveryPipelineCoverageDrilldownData, rsrFirstLevelTeams, rsrSelectedTeam, rsrDefaultTeam, ExpectedAcvData, renewalsSummaryData, renewalsSummaryDrilldownData, upsellCrossSellDrilldownData, strategicAccountsQualifiedPipelineData, strategicAccountsQualifiedPipelineDrilldownData, msspFulfillmentData } = state.app;

  return {
    company: user.company,
    Value_Label: user.Value_Label,
    forecastTrendData,
    forecastTrendDrilldownData,
    salesForecastCurrentQuarterData,
    forecastMovedOutData,
    forecastBestCaseData,
    salesForecastNextQuarterData,
    salesForecastNextQuarterPlus1Data,
    dealProgressionData,
    dealConversionData,
    upsellCrossSellData,
    qualifiedPipelineCoverageData,
    qualifiedPipelineCoverageDrilldownData,
    discoveryPipelineCoverageData,
    discoveryPipelineCoverageDrilldownData,
    rsrFirstLevelTeams,
    rsrSelectedTeam,
    rsrDefaultTeam,
    ExpectedAcvData,
    renewalsSummaryData,
    renewalsSummaryDrilldownData,
    upsellCrossSellDrilldownData,
    strategicAccountsQualifiedPipelineData,
    strategicAccountsQualifiedPipelineDrilldownData,
    msspFulfillmentData,
    helperTextCharts: user.elements.flatMap((item) => item.charts || []),
    user, // Pass the full user object
  };
};

const mapDispatchToProps = (dispatch) => ({
  getFirstLevelTeams: () => {
    dispatch({ type: "get_rsr_first_level_teams_request" });
    appService.getRegionalSalesReviewFirstLevelTeams().then(
      (json) => {
        dispatch({ type: "get_rsr_first_level_teams_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsr_first_level_teams_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsr_first_level_teams_failure", error });
      }
    );
  },
  getForecastTrendData: (filters = []) => {
    dispatch({ type: "get_forecast_trend_data_request" });
    appService.getRegionalSalesReviewForecastTrend(filters).then(
      (json) => {
        dispatch({ type: "get_forecast_trend_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_trend_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_trend_data_failure", error });
      }
    );
  },
  getSalesForecastCurrentQuarterData: (filters = []) => {
    dispatch({ type: "get_sales_forecast_current_quarter_data_request" });
    appService.getRegionalSalesReviewSalesForecastCurrentQuarter(filters).then(
      (json) => {
        dispatch({ type: "get_sales_forecast_current_quarter_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_sales_forecast_current_quarter_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_sales_forecast_current_quarter_data_failure", error });
      }
    );
  },
  getDealProgressionData: (filters = []) => {
    dispatch({ type: "get_deal_progression_data_request" });
    appService.getRegionalSalesReviewDealProgression(filters).then(
      (json) => {
        dispatch({ type: "get_deal_progression_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_deal_progression_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_deal_progression_data_failure", error });
      }
    );
  },
  getDealConversionData: (filters = []) => {
    dispatch({ type: "get_deal_conversion_data_request" });
    appService.getRegionalSalesReviewDealConversion(filters).then(
      (json) => {
        dispatch({ type: "get_deal_conversion_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_deal_conversion_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_deal_conversion_data_failure", error });
      }
    );
  },
  getUpsellCrossSellData: (filters = []) => {
    dispatch({ type: "get_upsell_cross_sell_data_request" });
    appService.getRegionalSalesReviewUpsellCrossSell(filters).then(
      (json) => {
        dispatch({ type: "get_upsell_cross_sell_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_upsell_cross_sell_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_upsell_cross_sell_data_failure", error });
      }
    );
  },
  getUpsellCrossSellDrilldown: (filters) => {
    dispatch({ type: "get_upsell_cross_sell_drilldown_request" });
    appService.getRegionalSalesReviewUpsellCrossSellDrilldown(filters).then(
      (json) => {
        dispatch({ type: "get_upsell_cross_sell_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_upsell_cross_sell_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_upsell_cross_sell_drilldown_failure", error });
      }
    );
  },
  clearUpsellCrossSellDrilldown: () => {
    dispatch({ type: "clear_upsell_cross_sell_drilldown" });
  },
  getForecastMovedOutData: (filters = []) => {
    dispatch({ type: "get_forecast_moved_out_data_request" });
    appService.getRegionalSalesReviewForecastMovedOut(filters).then(
      (json) => {
        dispatch({ type: "get_forecast_moved_out_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_moved_out_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_moved_out_data_failure", error });
      }
    );
  },
  getForecastBestCaseData: (filters = []) => {
    dispatch({ type: "get_forecast_best_case_data_request" });
    appService.getRegionalSalesReviewForecastBestCase(filters).then(
      (json) => {
        dispatch({ type: "get_forecast_best_case_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_best_case_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_best_case_data_failure", error });
      }
    );
  },
  getSalesForecastNextQuarterData: (filters = []) => {
    dispatch({ type: "get_sales_forecast_next_quarter_data_request" });
    appService.getRegionalSalesReviewSalesForecastNextQuarter(filters).then(
      (json) => {
        dispatch({ type: "get_sales_forecast_next_quarter_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_sales_forecast_next_quarter_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_sales_forecast_next_quarter_data_failure", error });
      }
    );
  },
  getSalesForecastNextQuarterPlus1Data: (filters = []) => {
    dispatch({ type: "get_sales_forecast_next_quarter_plus1_data_request" });
    appService.getRegionalSalesReviewSalesForecastNextQuarterPlus1(filters).then(
      (json) => {
        dispatch({ type: "get_sales_forecast_next_quarter_plus1_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_sales_forecast_next_quarter_plus1_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_sales_forecast_next_quarter_plus1_data_failure", error });
      }
    );
  },
  getQualifiedPipelineCoverageData: (filters = []) => {
    dispatch({ type: "get_qualified_pipeline_coverage_data_request" });
    appService.getPipelineReviewQualifiedPipelineCoverage(filters).then(
      (json) => {
        dispatch({ type: "get_qualified_pipeline_coverage_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_qualified_pipeline_coverage_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_qualified_pipeline_coverage_data_failure", error });
      }
    );
  },
  getDiscoveryPipelineCoverageData: (filters = []) => {
    dispatch({ type: "get_rsr_discovery_pipeline_coverage_data_request" });
    appService.getPipelineReviewDiscoveryPipelineCoverage(filters).then(
      (json) => {
        dispatch({ type: "get_rsr_discovery_pipeline_coverage_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsr_discovery_pipeline_coverage_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsr_discovery_pipeline_coverage_data_failure", error });
      }
    );
  },
  getDiscoveryPipelineCoverageDrilldown: (filters = []) => {
    dispatch({ type: "get_rsr_discovery_pipeline_coverage_drilldown_request" });
    appService.getPipelineReviewDiscoveryPipelineCoverageDrilldown(filters, 'Regional Sales Review Package - Qualified Pipeline').then(
      (json) => {
        dispatch({ type: "get_rsr_discovery_pipeline_coverage_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_rsr_discovery_pipeline_coverage_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_rsr_discovery_pipeline_coverage_drilldown_failure", error });
      }
    );
  },
  clearDiscoveryPipelineCoverageDrilldown: () => {
    dispatch({ type: "clear_rsr_discovery_pipeline_coverage_drilldown" });
  },
  getOpenOppData: (oppId) => {
    dispatch({ type: "get_expectedAcv_drilldown_data_request" });
    appService.getWaterfallOppData(oppId).then(
      (json) => {
        dispatch({ type: "get_expectedAcv_drilldown_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_expectedAcv_drilldown_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_expectedAcv_drilldown_data_failure", error });
      }
    );
  },
  clearExpectedAcvData: () => {
    dispatch({ type: "clear_expected_acv_data" });
  },
  getForecastTrendWonDrilldown: (filters = []) => {
    dispatch({ type: "get_forecast_trend_drilldown_request" });
    appService.getRegionalSalesReviewForecastTrendWonDrilldown(filters).then(
      (json) => {
        dispatch({ type: "get_forecast_trend_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_trend_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_trend_drilldown_failure", error });
      }
    );
  },
  getForecastTrendMostLikelyDrilldown: (filters = []) => {
    dispatch({ type: "get_forecast_trend_drilldown_request" });
    appService.getRegionalSalesReviewForecastTrendMostLikelyDrilldown(filters).then(
      (json) => {
        dispatch({ type: "get_forecast_trend_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_forecast_trend_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_forecast_trend_drilldown_failure", error });
      }
    );
  },
  clearForecastTrendDrilldown: () => {
    dispatch({ type: "clear_forecast_trend_drilldown" });
  },
  getQualifiedPipelineCoverageDrilldown: (filters = []) => {
    dispatch({ type: "get_qualified_pipeline_coverage_drilldown_request" });
    appService.getPipelineReviewQualifiedPipelineCoverageDrilldown(filters, 'Regional Sales Review Package - Qualified Pipeline').then(
      (json) => {
        dispatch({ type: "get_qualified_pipeline_coverage_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_qualified_pipeline_coverage_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_qualified_pipeline_coverage_drilldown_failure", error });
      }
    );
  },
  clearQualifiedPipelineCoverageDrilldown: () => {
    dispatch({ type: "clear_qualified_pipeline_coverage_drilldown" });
  },
  getRenewalsSummaryData: (filters = []) => {
    dispatch({ type: "get_renewals_summary_data_request" });
    appService.getRegionalSalesReviewRenewalsSummary(filters).then(
      (json) => {
        dispatch({ type: "get_renewals_summary_data_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_renewals_summary_data_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_renewals_summary_data_failure", error });
      }
    );
  },
  getRenewalsSummaryDrilldownData: (filters = []) => {
    dispatch({ type: "get_renewals_summary_drilldown_request" });
    appService.getRegionalSalesReviewRenewalsSummaryDrilldown(filters).then(
      (json) => {
        dispatch({ type: "get_renewals_summary_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_renewals_summary_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_renewals_summary_drilldown_failure", error });
      }
    );
  },
  clearRenewalsSummaryDrilldown: () => {
    dispatch({ type: "clear_renewals_summary_drilldown" });
  },
  getStrategicAccountsQualifiedPipelineData: (filters = []) => {
    dispatch({ type: "get_strategic_accounts_qualified_pipeline_request" });
    appService.getRegionalSalesReviewStrategicAccountsQualifiedPipeline(filters).then(
      (json) => {
        dispatch({ type: "get_strategic_accounts_qualified_pipeline_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_strategic_accounts_qualified_pipeline_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_strategic_accounts_qualified_pipeline_failure", error });
      }
    );
  },
  getStrategicAccountsQualifiedPipelineDrilldown: (filters = []) => {
    dispatch({ type: "get_strategic_accounts_qualified_pipeline_drilldown_request" });
    appService.getRegionalSalesReviewStrategicAccountsQualifiedPipelineDrilldown(filters).then(
      (json) => {
        dispatch({ type: "get_strategic_accounts_qualified_pipeline_drilldown_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({
            type: "get_strategic_accounts_qualified_pipeline_drilldown_failure",
            error: "Something went wrong",
          });
        else dispatch({ type: "get_strategic_accounts_qualified_pipeline_drilldown_failure", error });
      }
    );
  },
  clearStrategicAccountsQualifiedPipelineDrilldown: () => {
    dispatch({ type: "clear_strategic_accounts_qualified_pipeline_drilldown" });
  },
  getMsspFulfillmentData: (filters = []) => {
    dispatch({ type: "get_mssp_fulfillment_request" });
    appService.getRegionalSalesReviewMsspFulfillment(filters).then(
      (json) => {
        dispatch({ type: "get_mssp_fulfillment_success", json });
      },
      (error) => {
        if (typeof error === "object")
          dispatch({ type: "get_mssp_fulfillment_failure", error: "Something went wrong" });
        else dispatch({ type: "get_mssp_fulfillment_failure", error });
      }
    );
  },
});

const connectedRegionalSalesReviewPackage = connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(RegionalSalesReviewPackage));

export default connectedRegionalSalesReviewPackage;